<template>
  <div class="activity-page">
    <div class="page-header">
      <div class="page-avatar"><v-icon size="22" color="#fff">mdi-account-clock</v-icon></div>
      <div class="page-header-text">
        <h1 class="page-title">Aktywność pracowników</h1>
      </div>
    </div>

    <v-alert dense text type="info" class="mb-3 body-2">
      Raport liczony z logu audytowego — pokazuje pracę <strong>w panelu</strong>.
      „Okno pracy" to czas od pierwszej do ostatniej akcji w danym dniu, więc jest
      <strong>przybliżeniem</strong>, a nie ewidencją czasu pracy: przerwy wliczają się
      do okna, a praca poza panelem (telefon, wyjazd) w nim nie występuje.
      Ewidencja czasu pracy z sesjami i korektami jest w zakładce
      <router-link to="/work-time">Ewidencja czasu pracy</router-link>.
    </v-alert>

    <v-card class="mb-3">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-menu v-model="fromMenu" :close-on-content-click="false" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="filters.from" label="Od" outlined dense readonly
                  v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar" hide-details />
              </template>
              <v-date-picker v-model="filters.from" no-title first-day-of-week="1" locale="pl"
                @input="fromMenu = false" />
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <v-menu v-model="toMenu" :close-on-content-click="false" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="filters.to" label="Do" outlined dense readonly
                  v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar" hide-details />
              </template>
              <v-date-picker v-model="filters.to" no-title first-day-of-week="1" locale="pl"
                @input="toMenu = false" />
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="filters.userId" :items="userOptions" item-text="text" item-value="value"
              label="Pracownik" outlined dense clearable hide-details />
          </v-col>
          <v-col cols="12" md="3">
            <v-btn color="primary" block :loading="loading" @click="load">
              <v-icon left>mdi-refresh</v-icon> Pokaż
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert v-if="truncated" dense text type="warning" class="mb-3 body-2">
      Zakres zawiera {{ totalEntries }} zdarzeń — wczytano {{ loadedEntries }} najnowszych.
      Zawęź zakres dat albo wybierz pojedynczego pracownika, żeby raport był kompletny.
    </v-alert>

    <v-card v-if="loading">
      <v-card-text class="text-center py-8">
        <v-progress-circular indeterminate size="34" color="primary" />
        <div class="caption grey--text mt-3">Wczytuję zdarzenia…</div>
      </v-card-text>
    </v-card>

    <v-card v-else-if="!people.length">
      <v-card-text class="text-center grey--text py-8">
        Brak zdarzeń w wybranym zakresie.
      </v-card-text>
    </v-card>

    <template v-else>
      <v-card v-for="p in people" :key="p.key" class="mb-3">
        <v-card-title class="subtitle-1">
          <v-icon left color="primary">mdi-account</v-icon>
          {{ p.name }}
          <span class="caption grey--text ml-2">{{ p.role }}</span>
          <v-spacer />
          <v-chip x-small outlined class="ml-1">{{ p.totalActions }} akcji</v-chip>
          <v-chip x-small outlined class="ml-1">{{ p.days.length }} dni</v-chip>
          <v-chip x-small outlined class="ml-1">śr. {{ fmtDuration(p.avgWindowMin) }}/dzień</v-chip>
        </v-card-title>
        <v-card-subtitle class="caption">{{ p.email }}</v-card-subtitle>

        <v-card-text class="pt-0">
          <div v-for="d in p.days" :key="d.date" class="day-row">
            <div class="d-flex align-center flex-wrap">
              <div class="day-date">{{ fmtDay(d.date) }}</div>
              <div class="day-window">
                {{ d.first }} – {{ d.last }}
                <span class="grey--text">({{ fmtDuration(d.windowMin) }})</span>
              </div>
              <v-spacer />
              <div class="caption grey--text">{{ d.count }} akcji</div>
            </div>

            <div class="hour-bars mt-1">
              <div
                v-for="(c, h) in d.hours"
                :key="h"
                class="hour-bar"
                :class="{ 'hour-bar--empty': !c }"
                :style="c ? `height: ${Math.max(12, (c / d.peak) * 100)}%;` : ''"
                :title="`${String(h).padStart(2, '0')}:00 — ${c} akcji`"
              />
            </div>

            <div class="caption grey--text mt-1 day-top">
              {{ d.topLabels.join(' · ') || '—' }}
            </div>
          </div>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
const axios = require('axios');

const PAGE_SIZE = 200;
const MAX_PAGES = 25; // 5000 zdarzeń — dalej raport i tak przestaje być czytelny

function isoDate(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default {
  name: 'StaffActivityPage',

  data() {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 6);
    return {
      filters: { from: isoDate(from), to: isoDate(to), userId: null },
      fromMenu: false,
      toMenu: false,
      userOptions: [],
      people: [],
      loading: false,
      totalEntries: 0,
      loadedEntries: 0,
      truncated: false,
    };
  },

  async created() {
    await this.loadUsers();
    await this.load();
  },

  methods: {
    authHeaders() { return { Authorization: 'Bearer ' + localStorage.getItem('token') }; },
    apiUrl(path) { return process.env.VUE_APP_ROOT_API + path; },

    async loadUsers() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('company-admins'), headers: this.authHeaders() });
        const list = res.data?.data || res.data || [];
        this.userOptions = list.map((a) => ({
          text: `${a.name || a.firstName || ''} ${a.surname || a.lastName || ''}`.trim() || a.email,
          value: a.id,
        }));
      } catch (e) {
        this.userOptions = [];
      }
    },

    /** Wciąga wszystkie strony logu z zakresu (do limitu) i składa z nich raport. */
    async load() {
      this.loading = true;
      this.truncated = false;
      const entries = [];
      try {
        for (let page = 1; page <= MAX_PAGES; page++) {
          const params = new URLSearchParams();
          params.set('page', page);
          params.set('limit', PAGE_SIZE);
          if (this.filters.from) params.set('from', this.filters.from);
          if (this.filters.to) params.set('to', this.filters.to);
          if (this.filters.userId) params.set('userId', this.filters.userId);

          const res = await axios({
            method: 'get',
            url: this.apiUrl('audit-log?' + params.toString()),
            headers: this.authHeaders(),
          });
          const items = res.data?.items || [];
          if (page === 1) this.totalEntries = res.data?.total || items.length;
          entries.push(...items);
          if (items.length < PAGE_SIZE) break;
          if (page === MAX_PAGES && entries.length < this.totalEntries) this.truncated = true;
        }
        this.loadedEntries = entries.length;
        this.people = this.aggregate(entries);
      } catch (e) {
        console.error('Nie udało się wczytać logu aktywności:', e);
        this.people = [];
      }
      this.loading = false;
    },

    /**
     * Grupowanie po osobie i dniu. Kluczem osoby jest e-mail — jest zawsze
     * obecny i unikalny, w przeciwieństwie do samego imienia i nazwiska.
     */
    aggregate(entries) {
      const byPerson = new Map();

      for (const e of entries) {
        if (!e || !e.timestamp) continue;
        const key = e.userEmail || `${e.userName} ${e.userSurname}`;
        if (!key) continue;
        const ts = new Date(e.timestamp);
        if (isNaN(ts.getTime())) continue;
        const day = isoDate(ts);

        if (!byPerson.has(key)) {
          byPerson.set(key, {
            key,
            name: `${e.userName || ''} ${e.userSurname || ''}`.trim() || key,
            email: e.userEmail || '—',
            role: e.userRole || '—',
            totalActions: 0,
            dayMap: new Map(),
          });
        }
        const person = byPerson.get(key);
        person.totalActions++;

        if (!person.dayMap.has(day)) {
          person.dayMap.set(day, {
            date: day,
            minTs: ts.getTime(),
            maxTs: ts.getTime(),
            count: 0,
            hours: new Array(24).fill(0),
            labels: new Map(),
          });
        }
        const d = person.dayMap.get(day);
        d.count++;
        d.minTs = Math.min(d.minTs, ts.getTime());
        d.maxTs = Math.max(d.maxTs, ts.getTime());
        d.hours[ts.getHours()]++;
        const label = e.label || e.action;
        if (label) d.labels.set(label, (d.labels.get(label) || 0) + 1);
      }

      const out = [];
      for (const person of byPerson.values()) {
        const days = Array.from(person.dayMap.values())
          .sort((a, b) => (a.date < b.date ? 1 : -1))
          .map((d) => ({
            date: d.date,
            first: this.fmtTime(d.minTs),
            last: this.fmtTime(d.maxTs),
            windowMin: Math.round((d.maxTs - d.minTs) / 60000),
            count: d.count,
            hours: d.hours,
            peak: Math.max(...d.hours, 1),
            topLabels: Array.from(d.labels.entries())
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3)
              .map(([l, c]) => `${l} (${c})`),
          }));

        const totalWindow = days.reduce((s, d) => s + d.windowMin, 0);
        out.push({
          ...person,
          days,
          avgWindowMin: days.length ? Math.round(totalWindow / days.length) : 0,
        });
      }

      return out.sort((a, b) => b.totalActions - a.totalActions);
    },

    fmtTime(ms) {
      const d = new Date(ms);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    },
    fmtDay(iso) {
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('pl-PL', { weekday: 'short', day: '2-digit', month: '2-digit' });
    },
    fmtDuration(min) {
      if (!min) return '0 min';
      const h = Math.floor(min / 60);
      const m = min % 60;
      return h ? `${h} h ${m} min` : `${m} min`;
    },
  },
};
</script>

<style scoped>
.activity-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { display: flex; align-items: center; gap: 12px; padding: 4px 4px 18px; }
.page-avatar { width: 44px; height: 44px; border-radius: 14px; background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 6px 18px rgba(41,98,255,0.4); }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }

.day-row { padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.05); }
.day-row:last-child { border-bottom: none; }
.day-date { min-width: 110px; font-weight: 700; font-size: 13px; text-transform: capitalize; }
.day-window { font-size: 13px; font-variant-numeric: tabular-nums; }
.day-top { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.hour-bars { display: flex; align-items: flex-end; gap: 2px; height: 26px; }
.hour-bar { flex: 1 1 0; min-width: 0; background: #5478FF; border-radius: 2px; transition: height 0.2s; }
.hour-bar--empty { height: 2px; background: rgba(255,255,255,0.08); }

.activity-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.activity-page >>> .v-card .v-card__title { color: #fff !important; font-weight: 700 !important; font-size: 15px !important; }
.activity-page >>> .v-text-field--outlined fieldset, .activity-page >>> .v-select--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.activity-page >>> .v-text-field--outlined .v-input__slot, .activity-page >>> .v-select--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.activity-page >>> .v-label { color: #6E7691 !important; }
.activity-page >>> .v-text-field input, .activity-page >>> .v-select__selection { color: #fff !important; }
.activity-page >>> .v-btn:not(.v-btn--icon) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
</style>
